/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, April 13th 2022, 12:52:53 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { DocumentTemplate } from "lib/schemas";
import { PrimaryKeyValue } from "lib/types";
import Stack from "@mui/material/Stack";
import { OSActionButton } from "ui-widgets";
import { useServiceBuilderContext } from "service-builder/providers";
import { APIListProgressWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { COLORSCHEME } from "theme/styles";
import { useNavigate } from "react-router-dom";
import { resolveURLName } from "core/routes/utils";
import { useAPIListDocumentTemplates } from "service-builder/services";
import { EmptyDocumentTemplatesCTA } from "./EmptyDocumentTemplatesCTA";

export const DocumentTemplatesListView = () => {
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  const {
    items: documentTemplates,
    isMakingAPIRequest: isFetchingItems,
    httpResponseStatusCode: fetchItemsStatusCode,
    isAPIError: fetchItemsHasError,
  } = useAPIListDocumentTemplates(currentProcessDefinition.id);

  const navigate = useNavigate();

  const navigateToDocumentTemplate = React.useCallback(
    (documentTemplateId: PrimaryKeyValue<DocumentTemplate>) => {
      navigate(
        resolveURLName("documentTemplate", {
          serviceId: currentProcessDefinition.id,
          documentTemplateId: documentTemplateId,
        })
      );
    },
    [navigate, currentProcessDefinition.id]
  );

  return (
    <APIListProgressWrapper
      items={documentTemplates}
      showLoader={isFetchingItems}
      httpResponseStatusCode={fetchItemsStatusCode}
      isAPIError={fetchItemsHasError}
      noDataMessage={<EmptyDocumentTemplatesCTA />}
    >
      <Stack direction="column" spacing={2}>
        <List component="nav" aria-label="document-templates-list">
          {documentTemplates.map((documentTemplate) => (
            <ListItemButton color={COLORSCHEME.secondaryVariant} key={`${documentTemplate.id}`}>
              <Grid container>
                <Grid item md={1}>
                  <ReceiptIcon color="primary" />
                </Grid>
                <Grid item md={7}>
                  <ListItemText primary={documentTemplate.title} />
                </Grid>
                <Grid item md={4}>
                  <OSActionButton
                    actionStyle="configure"
                    variant="text"
                    onClick={() => navigateToDocumentTemplate(documentTemplate.id)}
                  >
                    Design Template
                  </OSActionButton>
                </Grid>
              </Grid>
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </APIListProgressWrapper>
  );
};
